import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import type { User as AppUser, CustomClaims } from '@shared/types';
import { buildUserProfile } from '../utils/userProfile';

interface SignInResult {
  user: User;
  claims: CustomClaims;
}

interface AuthContextType {
  user: User | null;
  userProfile: AppUser | null;
  customClaims: CustomClaims | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Езиците, които приложението наистина носи.
 *
 * ⚠️ Проверява се по СТОЙНОСТ, не по тип: `preferredLanguage` идва от Firestore,
 * а типът гарантира ключа, не съдържанието му. Стар или ръчно редактиран запис
 * може да носи какво ли не; непознат език НЕ сменя нищо, вместо да остави екрана
 * с празни преводи.
 */
const ПОДДЪРЖАНИ = ['bg', 'de', 'en'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<AppUser | null>(null);
  const [customClaims, setCustomClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  /**
   * 🔴 Езикът на екрана идва от ПРОФИЛА — това е брънката, която липсваше.
   *
   * `preferredLanguage` се записваше при вход и в настройките, бекендът го четеше
   * на 42 места (писмата тръгват на него), а интерфейсът никога не го прочете.
   * Резултатът: родителят получава напомняне на немски, натиска връзката в него
   * и попада на БЪЛГАРСКИ екран — приложението знае езика му и не го ползва.
   *
   * ⚠️ Тук, а не в отделен компонент: това е единственото място, през което
   * минава ВСЯКА роля и ВСЯКА лента. Сложено в един Layout, останалите мълчат.
   */
  useEffect(() => {
    const предпочитан = userProfile?.preferredLanguage;
    if (!предпочитан) return;
    if (!(ПОДДЪРЖАНИ as readonly string[]).includes(предпочитан)) return;
    if (i18n.language === предпочитан) return;
    void i18n.changeLanguage(предпочитан);
  }, [userProfile?.preferredLanguage, i18n]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Get custom claims
        const idTokenResult = await firebaseUser.getIdTokenResult();
        setCustomClaims(idTokenResult.claims as unknown as CustomClaims);

        // Get user profile from Firestore
        try {
          const claims = idTokenResult.claims as unknown as CustomClaims;

          // Helper: look up parent name from families collection
          const getNameFromFamily = async (): Promise<string | undefined> => {
            if (!claims.schoolId || !claims.familyId) return undefined;
            try {
              const familyDoc = await getDoc(
                doc(db, 'schools', claims.schoolId, 'families', claims.familyId)
              );
              if (!familyDoc.exists()) return undefined;
              const family = familyDoc.data();
              const email = firebaseUser.email;
              if (family.parent1?.email === email) return family.parent1?.name;
              if (family.parent2?.email === email) return family.parent2?.name;
            } catch {
              // ignore — family lookup is best-effort
            }
            return undefined;
          };

          // Документът носи само предпочитанията (език, име…); самоличността — от claims.
          // Двата случая (с и без документ) минават през ЕДИН израз — `buildUserProfile`.
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const docData = userDoc.exists() ? (userDoc.data() as Record<string, unknown>) : null;
          const docName = typeof docData?.displayName === 'string' ? docData.displayName : '';
          const displayName =
            docName || firebaseUser.displayName || (await getNameFromFamily());
          setUserProfile(
            buildUserProfile({
              uid: firebaseUser.uid,
              authEmail: firebaseUser.email,
              claims,
              doc: docData,
              displayName: displayName || undefined,
            })
          );
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setCustomClaims(null);
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string): Promise<SignInResult> => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const idTokenResult = await credential.user.getIdTokenResult();
    const claims = idTokenResult.claims as unknown as CustomClaims;
    return { user: credential.user, claims };
  };

  const signUp = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    // Firebase пише писмото на `auth.languageCode`; без него — на езика по
    // подразбиране на проекта (en), тоест родителят получаваше английско писмо,
    // което не разпознава. Езикът на писмото = езикът на екрана, от който е
    // поискано; връзката в писмото носи същия `lang` към /auth/action.
    // Измерено на staging 19.10: Firebase има и bg, и de превод на шаблона.
    const език = (i18n.language || 'bg').split('-')[0];
    auth.languageCode = (ПОДДЪРЖАНИ as readonly string[]).includes(език) ? език : 'bg';
    await sendPasswordResetEmail(auth, email);
  };

  const value = {
    user,
    userProfile,
    customClaims,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
